// WebSocket event types
export const WS_EVENTS = {
  // Client -> Server
  CHAT_MESSAGE: 'chat:message',
  CHAT_CANCEL: 'chat:cancel',
  PING: 'ping',

  // Server -> Client
  CHAT_CHUNK: 'chat:chunk',
  CHAT_COMPLETE: 'chat:complete',
  CHAT_ERROR: 'chat:error',
  PONG: 'pong',
  SESSION_CONNECTED: 'session:connected'
};

// Configuration
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BASE_DELAY_MS = 1000;
const RECONNECT_MAX_JITTER_MS = 1000; // Random jitter to prevent thundering herd
const PING_INTERVAL_MS = 30000;
const MAX_QUEUE_SIZE = 10; // Max messages to buffer when disconnected

/**
 * Subprotocols for the chat socket. The server only lets a session's owner in,
 * and browsers cannot set an Authorization header on a WebSocket, so the token
 * rides as 'etch.bearer.<token>' next to 'etch' — the one the server answers
 * with. Without a token (standalone install) nothing is offered.
 */
export function socketProtocols(token) {
  return token ? ['etch', `etch.bearer.${token}`] : [];
}

function storedToken() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('etch.token') : null;
  } catch {
    return null;
  }
}

export class WebSocketClient {
  constructor() {
    this.ws = null;
    this.sessionId = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = MAX_RECONNECT_ATTEMPTS;
    this.reconnectDelay = RECONNECT_BASE_DELAY_MS;
    this.pingInterval = null;
    this.messageQueue = []; // Buffer for messages when disconnected
  }

  connect(sessionId) {
    return new Promise((resolve, reject) => {
      // A socket still opening or open belongs to the last connect. Drop it
      // first, or its late close reports on this one and starts a reconnect.
      this.dropSocket();

      this.sessionId = sessionId;
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const url = `${protocol}//${host}/ws?sessionId=${sessionId}`;

      const ws = new WebSocket(url, socketProtocols(storedToken()));
      this.ws = ws;
      // Once replaced, a socket's events report nothing.
      const isCurrent = () => this.ws === ws;

      ws.onopen = () => {
        if (!isCurrent()) return;
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        this.startPingInterval();
        this.flushMessageQueue();
        this.emit('open', {});
        resolve();
      };

      ws.onclose = (event) => {
        if (!isCurrent()) return;
        console.log('WebSocket closed:', event.code, event.reason);
        this.stopPingInterval();
        this.emit('close', { code: event.code, reason: event.reason });

        // Auto-reconnect if not intentionally closed
        if (event.code !== 1000 && this.sessionId) {
          this.attemptReconnect();
        }
      };

      ws.onerror = (error) => {
        if (!isCurrent()) return;
        console.error('WebSocket error:', error);
        this.emit('error', error);
        reject(error);
      };

      ws.onmessage = (event) => {
        if (!isCurrent()) return;
        try {
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };
    });
  }

  disconnect() {
    this.sessionId = null;
    this.messageQueue = []; // Clear queued messages
    this.dropSocket();
  }

  dropSocket() {
    this.stopPingInterval();
    if (this.ws) {
      const ws = this.ws;
      this.ws = null;
      ws.close(1000, 'Client disconnect');
    }
  }

  handleMessage(message) {
    const { type, payload } = message;
    this.emit(type, payload);
  }

  send(type, payload = {}, queueIfDisconnected = false) {
    const message = { type, payload };

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else if (queueIfDisconnected && this.sessionId) {
      // Queue message for later if we have a session (will reconnect)
      if (this.messageQueue.length < MAX_QUEUE_SIZE) {
        this.messageQueue.push(message);
        console.log(`Message queued (${this.messageQueue.length}/${MAX_QUEUE_SIZE})`);
      } else {
        console.warn('Message queue full, dropping message');
      }
    }
  }

  flushMessageQueue() {
    if (this.messageQueue.length === 0) return;

    console.log(`Flushing ${this.messageQueue.length} queued messages`);
    const queue = [...this.messageQueue];
    this.messageQueue = [];

    for (const message of queue) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(message));
      }
    }
  }

  sendChatMessage(question, context = null, personaPrompt = null) {
    // Queue chat messages if disconnected (will send on reconnect)
    const payload = { question, context };
    if (personaPrompt) payload.personaPrompt = personaPrompt;
    this.send(WS_EVENTS.CHAT_MESSAGE, payload, true);
  }

  cancelChat() {
    this.send(WS_EVENTS.CHAT_CANCEL);
  }

  // Event emitter
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  // Ping/pong for keepalive
  startPingInterval() {
    this.pingInterval = setInterval(() => {
      this.send(WS_EVENTS.PING);
    }, PING_INTERVAL_MS);
  }

  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Reconnection logic with exponential backoff and jitter
  attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      this.emit('reconnect_failed', {});
      return;
    }

    this.reconnectAttempts++;

    // Exponential backoff with random jitter to prevent thundering herd
    const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    const jitter = Math.random() * RECONNECT_MAX_JITTER_MS;
    const delay = baseDelay + jitter;

    console.log(`Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      if (this.sessionId) {
        this.connect(this.sessionId).catch(() => {
          // Will trigger another reconnect attempt on close
        });
      }
    }, delay);
  }

  get isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

export const wsClient = new WebSocketClient();
export default wsClient;
